import { useCallback, useState } from 'react';
import { Title, useTranslate } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { Psychology } from '@mui/icons-material';
import { SymptomForm } from './SymptomForm';
import { LiveStreamView } from './LiveStreamView';
import { ReportCard } from './ReportCard';
import { InvestigationHistoryList } from './InvestigationHistoryList';
import { useInvestigationStream } from './useInvestigationStream';
import { PromoteToPlaybookButton } from '../ai-playbooks';
import type { InvestigationInput, InvestigationReport } from './types';

// Top-level AI Troubleshooting page: owns the run/stream state machine (via useInvestigationStream)
// and the history. Layout uses MUI v7 Box flexbox (no Grid).
export const AiTroubleshootingPage = () => {
  const translate = useTranslate();
  // Bumped after a run reaches a persisted terminal state so the history re-fetches page 1.
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const onComplete = useCallback((_report: InvestigationReport) => {
    // The row is persisted backend-side by the engine (REF-05 s.4); refresh history page 1.
    setHistoryRefresh((n) => n + 1);
  }, []);

  const { status, steps, report, error, run, abort } = useInvestigationStream(onComplete);

  const handleRun = useCallback(
    (input: InvestigationInput) => {
      run(input);
    },
    [run],
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%' }}>
      <Title title={translate('aiTroubleshooting.title')} />

      <Box
        sx={{
          mb: { xs: 2, sm: 3 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        <Psychology sx={{ fontSize: { xs: 28, sm: 40 }, color: 'primary.main' }} />
        <Box>
          <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
            {translate('aiTroubleshooting.pageTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('aiTroubleshooting.subtitle')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 3 } }}>
        {/* Left column: input + live stream + report */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SymptomForm onRun={handleRun} running={status === 'running'} />
          <LiveStreamView status={status} steps={steps} error={error} onAbort={abort} />
          {report && (
            <ReportCard
              report={report}
              actions={
                <PromoteToPlaybookButton
                  investigation={{
                    investigationId: report.investigationId,
                    status: report.status,
                    rootCauseCategory: report.rootCauseCategory,
                  }}
                />
              }
            />
          )}
        </Box>

        {/* Right column: history */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <InvestigationHistoryList refreshSignal={historyRefresh} />
        </Box>
      </Box>
    </Box>
  );
};

export default AiTroubleshootingPage;
